import { BackgroundJob } from "@/types/BackgroundJob";
import { OptionalParamsBackgroundJobs } from "@/types/OptionalParamsBackgroundJobs";
import { apiDelete, apiGet, apiPost, apiPostWithHeaders, apiPut } from "../_apiShim";

const BASE_PATH = "/api/v1/background-jobs";

/**
 * The response header the backend sets when a submit's `clientRequestId` was
 * already known and the ORIGINAL job is being answered — nothing new was
 * queued. Lower-cased, as axios keys response headers. Readable in a browser
 * only once the backend lists it in its CORS exposed headers; until then it
 * is simply absent, which callers treat as "no signal".
 */
export const IDEMPOTENT_REPLAY_HEADER = "x-veltric-idempotent-replay";

/** True when the submit response says this is a replay of an earlier submit. */
export function isIdempotentReplay(headers: Record<string, string> | undefined): boolean {
  if (!headers) return false;
  for (const [k, v] of Object.entries(headers)) {
    if (k.toLowerCase() === IDEMPOTENT_REPLAY_HEADER) return String(v).toLowerCase() === "true";
  }
  return false;
}

export type SubmitBackgroundJobOptions = {
  /** Receives the response headers (lower-cased) once the submit answered. */
  onResponseHeaders?: (headers: Record<string, string>) => void;
  /**
   * Abandon the submit (SMS manual send: a 30 s client timeout). An aborted
   * submit has NO answer — the job may or may not exist; the caller says
   * "status unclear" and retries with the same idempotency key.
   */
  signal?: AbortSignal;
};

/**
 * Submit a new background job.
 * POST /api/v1/background-jobs/{eventId}
 */
export async function submitBackgroundJob(
  eventId: string,
  taskType: string,
  optionalParams: OptionalParamsBackgroundJobs,
  opts?: SubmitBackgroundJobOptions,
): Promise<string> {
  const url = `${BASE_PATH}/${eventId}`;
  const headers: Record<string, string> = {
    "Cache-Control": "no-store",
  };
  const body = {
    taskType,
    optionalParams,
  };
  // The 4th argument only when a signal is given, so every existing caller's
  // call keeps its exact three-argument shape.
  const { data, headers: responseHeaders } = opts?.signal
    ? await apiPostWithHeaders<string>(url, body, headers, { signal: opts.signal })
    : await apiPostWithHeaders<string>(url, body, headers);
  opts?.onResponseHeaders?.(responseHeaders);
  return data;
}

/**
 * Submit a company-scope background job (EXPORT_EVENTS_* task types).
 * POST /api/v1/background-jobs/company/{companyId}
 *
 * The backend stores companyId in the BackgroundJob.eventId slot (scopeId).
 * Status polling + cancel + delete use the existing endpoints with companyId
 * passed where eventId is expected.
 */
export async function submitBackgroundJobCompany(
  companyId: string,
  taskType: string,
  optionalParams: OptionalParamsBackgroundJobs
): Promise<string> {
  const url = `${BASE_PATH}/company/${companyId}`;
  const headers: Record<string, string> = {
    "Cache-Control": "no-store",
  };
  return await apiPost<string>(
    url,
    {
      taskType,
      optionalParams,
    },
    headers
  );
}

/**
 * Get background job status by ID with optional field projection.
 * GET /api/v1/background-jobs/{eventId}/{backgroundJobId}?fields=...
 */
export async function getBackgroundJobStatus(
  eventId: string,
  backgroundJobId: string,
  fields: string,
): Promise<BackgroundJob> {
  const url = `${BASE_PATH}/${eventId}/${backgroundJobId}?fields=${encodeURIComponent(
    fields
  )}`;

  const headers: Record<string, string> = {
    "Cache-Control": "no-store",
  };
  return await apiGet<BackgroundJob>(url, headers, true);
}

/**
 * Get a company-scope background job status.
 * GET /api/v1/background-jobs/company/{companyId}/{backgroundJobId}?fields=...
 */
export async function getBackgroundJobStatusCompany(
  companyId: string,
  backgroundJobId: string,
  fields: string,
): Promise<BackgroundJob> {
  const url = `${BASE_PATH}/company/${companyId}/${backgroundJobId}?fields=${encodeURIComponent(
    fields
  )}`;

  const headers: Record<string, string> = {
    "Cache-Control": "no-store",
  };
  return await apiGet<BackgroundJob>(url, headers, true);
}

/**
 * Delete a background job by ID.
 * DELETE /api/v1/background-jobs/{eventId}/{backgroundJobId}
 */
export async function deleteBackgroundJob(
  eventId: string,
  backgroundJobId: string,
): Promise<void> {
  const url = `${BASE_PATH}/${eventId}/${backgroundJobId}`;

  const headers: Record<string, string> = {};
  await apiDelete<void>(url, headers);
}

/**
 * Delete a company-scope background job.
 * DELETE /api/v1/background-jobs/company/{companyId}/{backgroundJobId}
 */
export async function deleteBackgroundJobCompany(
  companyId: string,
  backgroundJobId: string,
): Promise<void> {
  const url = `${BASE_PATH}/company/${companyId}/${backgroundJobId}`;

  const headers: Record<string, string> = {};
  await apiDelete<void>(url, headers);
}

/**
 * Cancel a running background job.
 * PUT /api/v1/background-jobs/{eventId}/{backgroundJobId}/cancel
 */
export async function cancelBackgroundJob(
  eventId: string,
  backgroundJobId: string,
): Promise<void> {
  const url = `${BASE_PATH}/${eventId}/${backgroundJobId}/cancel`;

  const headers: Record<string, string> = {};
  await apiPut<void>(url, {}, headers);
}

/**
 * Cancel a running company-scope background job.
 * PUT /api/v1/background-jobs/company/{companyId}/{backgroundJobId}/cancel
 */
export async function cancelBackgroundJobCompany(
  companyId: string,
  backgroundJobId: string,
): Promise<void> {
  const url = `${BASE_PATH}/company/${companyId}/${backgroundJobId}/cancel`;

  const headers: Record<string, string> = {};
  await apiPut<void>(url, {}, headers);
}
